import { z } from 'zod';
import { ExpenditureCartModel } from './expenditure-cart';

/// ////////////////////////////////////////////
/// Model
/// ////////////////////////////////////////////

const SubProjectBaseModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  priorId: z.string().uuid().nullable(),
  projectId: z.string().uuid(),
  name: z.string(),
});

const SubProjectModel = SubProjectBaseModel.merge(
  z.object({
    expenditureCarts: ExpenditureCartModel.array(),
  }),
);

export const SubProjectsModel = SubProjectModel.array();

export const MutateSubProjectResponseModel = SubProjectModel.pick({
  id: true,
  createdAt: true,
  updatedAt: true,
  priorId: true,
  name: true,
  projectId: true,
});

export const FundBusinessForLocalGovernmentModel = z.object({
  decidedNumberOfSubsidy: z.number().int().nonnegative().nullable(),
  decidedAmountOfSubsidy: z.number().nullable(),
  negativeDecidedAmountOfSubsidyCount: z.number().nullish(),
  decidedNumberOfInvestment: z.number().int().nonnegative().nullable(),
  decidedAmountOfInvestment: z.number().nullable(),
  negativeDecidedAmountOfInvestmentCount: z.number().nullish(),
  decidedNumberOfLoan: z.number().int().nonnegative().nullable(),
  decidedAmountOfLoan: z.number().nullable(),
  negativeDecidedAmountOfLoanCount: z.number().nullish(),
  decidedNumberOfDebtGuarantee: z.number().int().nonnegative().nullable(),
  decidedAmountOfDebtGuarantee: z.number().nullable(),
  negativeDecidedAmountOfDebtGuaranteeCount: z.number().nullish(),
  decidedNumberOfOthers: z.number().int().nonnegative().nullable(),
  decidedAmountOfOthers: z.number().nullable(),
  negativeDecidedAmountOfOthersCount: z.number().nullish(),
  balanceNumberOfInvestment: z.number().int().nonnegative().nullable(),
  balanceAmountOfInvestment: z.number().nullable(),
  negativeBalanceAmountOfInvestmentCount: z.number().nullish(),
  balanceNumberOfLoan: z.number().int().nonnegative().nullable(),
  balanceAmountOfLoan: z.number().nullable(),
  negativeBalanceAmountOfLoanCount: z.number().nullish(),
  balanceNumberOfDebtGuarantee: z.number().int().nonnegative().nullable(),
  balanceAmountOfDebtGuarantee: z.number().nullable(),
  negativeBalanceAmountOfDebtGuaranteeCount: z.number().nullish(),
  beginningOfTheFundBalance: z.number().nullable(),
  negativeBeginningOfTheFundBalanceCount: z.number().nullish(),
  nationalEquivalentOfBeginningOfTheFundBalance: z.number().nullable(),
  negativeNationalEquivalentOfBeginningOfTheFundBalanceCount: z
    .number()
    .nullish(),
  incomeAmount: z.number().nullable(),
  negativeIncomeAmountCount: z.number().nullish(),
  nationalEquivalentOfIncomeAmount: z.number().nullable(),
  negativeNationalEquivalentOfIncomeAmountCount: z.number().nullish(),
  grantedAmountByTheCountryFromInitial: z.number().nullable(),
  negativeGrantedAmountByTheCountryFromInitialCount: z.number().nullish(),
  grantedAmountByTheCountryFromSupplementary: z.number().nullable(),
  negativeGrantedAmountByTheCountryFromSupplementaryCount: z.number().nullish(),
  grantedAmountByTheCountryFromReserve: z.number().nullable(),
  negativeGrantedAmountByTheCountryFromReserveCount: z.number().nullish(),
  otherIncomeAmount: z.number().nullable(),
  negativeOtherIncomeAmountCount: z.number().nullish(),
  businessExpense: z.number().nullable(),
  negativeBusinessExpenseCount: z.number().nullish(),
  amountReturnedToTheNationalTreasury: z.number().nullable(),
  negativeAmountReturnedToTheNationalTreasuryCount: z.number().nullish(),
  nationalEquivalentOfTheEndOfTheFundBalance: z.number().nullable(),
  negativeNationalEquivalentOfTheEndOfTheFundBalanceCount: z.number().nullish(),
});

export const SubProjectForKSModel = SubProjectBaseModel.merge(
  z.object({
    localFundOwner: z.string().nullable(),
    localFundOverview: z.string().nullable(),
    fundBusinessForLocalGovernment:
      FundBusinessForLocalGovernmentModel.nullable(),
  }),
);

export const SubProjectsForKSModel = SubProjectForKSModel.array();

/// ////////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////////

export const SubProjectFormSchema = z.object({
  name: z.string().min(1),
});

export const SubProjectFundInfoFormSchema = z.object({
  name: z.string().min(1),
  localFundOwner: z.string().min(1),
  localFundOverview: z.string().min(1),
});

export const SubProjectFundInfoUpdateFormSchema =
  SubProjectFundInfoFormSchema.extend({
    subProjectId: z.string().uuid(),
  });

export const SubProjectUpdateFundBusinessFormSchema =
  FundBusinessForLocalGovernmentModel.partial();

export const SubProjectUpdateFundBusinessMutationSchema = z.object({
  fundBusinessForLocalGovernment: SubProjectUpdateFundBusinessFormSchema,
});
